import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { map } from "lodash";
import { useAccount } from "@/features/workspace/hooks/useAccount";
import { AssessmentOrder, Person } from "@/features/people/graphql/generated";
import type { DataItemProps } from "@/shared/widgets/DataSection";
import type { DropdownMenuItem } from "@/shared/widgets/DropdownMenu";
import { useAssessmentSurveys } from "./useAssessmentSurveys";
import {
  AssessmentSurveyConfig,
  AssessmentSurveyType,
} from "@/shared/constants/assessmentSurveys";

const ASSESSMENT_ORDER_LABELS: Partial<
  Record<AssessmentSurveyType, { label: string; tag: string }>
> = {
  [AssessmentSurveyType.MAP]: { label: "MAP", tag: "Personality" },
  [AssessmentSurveyType.MATCH_V_V2]: { label: "MATCH_V", tag: "Values" },
  [AssessmentSurveyType.AP_COMPETENCIES]: {
    label: "AP Competencies",
    tag: "Competencies",
  },
  [AssessmentSurveyType.IPIP_NEO_BIG_5]: {
    label: "IPIP NEO Big 5",
    tag: "Personality",
  },
  [AssessmentSurveyType.SHWARTZ_VALUES]: {
    label: "Schwartz Values",
    tag: "Values",
  },
  [AssessmentSurveyType.WISNIO_COMPETENCIES]: {
    label: "Wisnio Competencies",
    tag: "Competencies",
  },
};

const getAssessmentOrderLabelAndTag = (
  t: (key: string) => string,
  assessmentType: string
) => {
  const config =
    ASSESSMENT_ORDER_LABELS[assessmentType as AssessmentSurveyType];
  if (config) {
    return {
      label: config.label,
      tag: t(`survey.tags.${config.tag.toLowerCase()}`) || config.tag,
    };
  }
  return {
    label: assessmentType,
    tag: t("survey.default_tag"),
  };
};

export const useAssessmentOrdersData = (
  person: Person,
  config: AssessmentSurveyType[]
) => {
  const { t } = useTranslation();
  const { getAccountAssessmentSurveys } = useAccount();
  const { handleRetakeSurvey, loading } = useAssessmentSurveys();

  const assessmentSurveysConfig = getAccountAssessmentSurveys(config);

  const hasAssessmentSurveys = !!assessmentSurveysConfig?.length;

  const personAssessmentOrders = (person?.assessment_orders ||
    []) as AssessmentOrder[];

  const assessmentOrdersDataList = useMemo(() => {
    if (!hasAssessmentSurveys) return [];

    return map(assessmentSurveysConfig, (config) => {
      const assessmentType = config.type;
      const { label, tag } = getAssessmentOrderLabelAndTag(t, assessmentType);

      // Find the most recent order for this assessment type
      const matchingOrders = personAssessmentOrders
        .filter((order) => order?.assessment_type === assessmentType)
        .sort((a, b) => {
          const dateA = Number(a?.completed_at || a?.created_at || 0);
          const dateB = Number(b?.completed_at || b?.created_at || 0);
          return dateB - dateA;
        });

      // Get the most recent pending order, or the most recent completed order
      const pendingOrder = matchingOrders.find(
        (order) => order?.status === "pending"
      );
      const completedOrder = matchingOrders.find(
        (order) => order?.status === "completed" && !!order?.completed_at
      );

      const isCompleted = !!completedOrder;
      const isPending = !!pendingOrder && !isCompleted;
      const neverStarted = matchingOrders.length === 0;

      const formatCompletedDate = (timestamp: string) => {
        const date = new Date(Number(timestamp));
        return date.toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
          year: "numeric",
        });
      };

      const getStatusLabel = () => {
        if (isCompleted && completedOrder?.completed_at) {
          return `${t("status.completed")} ${formatCompletedDate(
            completedOrder.completed_at
          )}`;
        }
        if (neverStarted) {
          return t("status.not_started");
        }
        return t("status.pending");
      };

      const handleClick = () => {
        if (neverStarted) {
          handleRetakeSurvey(assessmentType, null);
          return;
        }
        if (isPending && pendingOrder?.assessment_link) {
          window.location.assign(pendingOrder.assessment_link);
        }
      };

      // Only show retake menu for completed assessments
      const menuItems: DropdownMenuItem[] | undefined = isCompleted
        ? [
            {
              label: t("profile.retake_survey"),
              icon: "Play",
              onClick: () =>
                handleRetakeSurvey(
                  assessmentType,
                  pendingOrder?.assessment_link
                ),
            },
          ]
        : undefined;

      return {
        label,
        icon: isCompleted ? ("CheckGreen" as const) : undefined,
        tagLabel: `${tag} · ${getStatusLabel()}`,
        removable: false,
        onClick:
          neverStarted || (isPending && pendingOrder?.assessment_link)
            ? handleClick
            : undefined,
        menuItems,
      };
    }) as DataItemProps[];
  }, [
    hasAssessmentSurveys,
    assessmentSurveysConfig,
    personAssessmentOrders,
    t,
    handleRetakeSurvey,
  ]);

  return {
    hasAssessmentSurveys,
    assessmentOrdersDataList,
    loading,
  };
};
